import { setStorageOwner } from '@/lib/private-storage';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { PalmAnalysisResult } from '@/services/palmAnalysisService';
import API_CONFIG from '@/lib/api-config';
import { clearSignupProfileDraft, getSignupProfileDraft } from '@/lib/signup-profile-draft';

export interface UserProfile {
  name: string;
  userId?: string;
  email?: string;
  birthDate: string; // YYYY-MM-DD
  birthTime: string; // HH:MM
  birthTimeConfidence?: 'exact' | 'approximate' | 'unknown';
  birthPlace: string;
  birthLatitude?: number;
  birthLongitude?: number;
  birthTimezone?: string;
  birthLocationLabel?: string;
  primaryIntention?: 'self_knowledge' | 'love' | 'career' | 'daily_ritual';
  notificationPreferences?: {
    daily?: boolean;
    weekly?: boolean;
    specialEvents?: boolean;
    sound?: boolean;
    palm?: boolean;
    browserPermission?: NotificationPermission | 'unsupported';
    pushSubscribed?: boolean;
    updatedAt?: string;
  };
  palmReadings: {
    id: string;
    date: string;
    result: PalmAnalysisResult;
  }[];
  isDemoMode: boolean;
  isPremium: boolean;
  isLite: boolean;
  isAdmin: boolean;
  bonusReadings?: number;
  referralCode?: string;
  profileImage?: string;
}

interface UserState extends UserProfile {
  // Actions
  updateProfile: (data: Partial<UserProfile>) => Promise<void>;
  addPalmReading: (reading: { id: string; date: string; result: PalmAnalysisResult }) => void;
  saveReading: (result: PalmAnalysisResult) => Promise<void>;
  toggleDemoMode: () => void;
  getAge: () => number;
  checkAuth: () => void;

  // Auth
  accessToken?: string;
  isAuthenticated: boolean;
  streak?: number;
  setStreak: (count: number) => void;
  login: (token: string, user: Partial<UserProfile> & { streak?: number }) => Promise<void>;
  logout: () => void;
}

const emptyProfile = {
  name: '',
  userId: undefined,
  email: undefined,
  birthDate: '',
  birthTime: '',
  birthTimeConfidence: 'unknown' as const,
  birthPlace: '',
  birthLatitude: undefined,
  birthLongitude: undefined,
  birthTimezone: undefined,
  birthLocationLabel: undefined,
  primaryIntention: undefined,
  notificationPreferences: undefined,
  palmReadings: [],
  isDemoMode: false,
  isPremium: false,
  isLite: false,
  isAdmin: false,
  bonusReadings: 0,
  referralCode: undefined,
  profileImage: undefined,
  accessToken: undefined,
  isAuthenticated: false,
  streak: 0,
};
let generation = 0;
let sessionController: AbortController | null = null;

export const useUserStore = create<UserState>()(
  persist(
    (set, get) => ({
      ...emptyProfile,
      login: async (token, user) => {
        const version = ++generation;
        sessionController?.abort();
        const controller = new AbortController();
        sessionController = controller;
        const current = () => generation === version && !controller.signal.aborted;
        const draft = getSignupProfileDraft();
        const ownDraft =
          draft?.email && user.email && draft.email.toLowerCase() === user.email.toLowerCase()
            ? draft
            : null;
        const sameUser = get().userId === user.userId;
        setStorageOwner(user.userId);
        set({
          ...(sameUser ? {} : emptyProfile),
          userId: user.userId,
          accessToken: token,
          isAuthenticated: true,
          isDemoMode: false,
        });
        const headers = { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token };
        try {
          if (ownDraft) {
            const response = await fetch(API_CONFIG.user.profile(), {
              method: 'POST',
              headers,
              signal: controller.signal,
              body: JSON.stringify(ownDraft),
            });
            if (!response.ok) throw new Error('Profile could not be saved');
            if (current()) clearSignupProfileDraft();
          }
          let response = await fetch(API_CONFIG.user.profile(), {
            headers,
            signal: controller.signal,
            cache: 'no-store',
          });
          if (!response.ok) throw new Error('Profile could not be loaded');
          let profile = await response.json();
          if (!profile?.userId) {
            response = await fetch(API_CONFIG.user.profile(), {
              method: 'POST',
              headers,
              signal: controller.signal,
              body: JSON.stringify({ name: user.name || '', email: user.email }),
            });
            if (!response.ok) throw new Error('Profile could not be created');
            profile = await response.json();
          }
          if (current() && profile.userId === user.userId) {
            set({ ...profile, accessToken: token, isAuthenticated: true });
          }
        } catch (error) {
          if (current()) console.error('Profile synchronization failed', error);
        }
      },
      logout: () => {
        generation++;
        sessionController?.abort();
        sessionController = null;
        set({ ...emptyProfile });
        try {
          setStorageOwner();
          clearSignupProfileDraft();
        } catch (error) {
          console.warn('Browser storage could not be cleared', error);
        }
      },
      updateProfile: async (data) => {
        const { accessToken, userId, isDemoMode } = get();
        const version = generation;
        if (isDemoMode) {
          set(data);
          return;
        }
        if (!accessToken || !userId) throw new Error('Please sign in again.');
        const response = await fetch(API_CONFIG.user.profile(), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + accessToken },
          signal: sessionController?.signal,
          body: JSON.stringify(data),
        });
        if (!response.ok) throw new Error('Profile could not be saved. Try again.');
        const profile = await response.json();
        if (generation !== version || get().userId !== userId)
          throw new Error('Account changed before save completed.');
        set(profile);
      },
      addPalmReading: (reading) =>
        set((state) => ({ palmReadings: [reading, ...state.palmReadings] })),
      saveReading: async (result) => {
        const { accessToken, userId } = get();
        const version = generation;
        if (!accessToken) throw new Error('Please sign in again.');
        const id = crypto.randomUUID(),
          date = new Date().toISOString();
        const response = await fetch(API_CONFIG.user.palmReadings(), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + accessToken },
          signal: sessionController?.signal,
          body: JSON.stringify({ id, date, ...result }),
        });
        if (!response.ok) throw new Error('Reading backup could not be saved.');
        if (generation === version && get().userId === userId)
          get().addPalmReading({ id, date, result });
      },
      toggleDemoMode: () => set((state) => ({ isDemoMode: !state.isDemoMode })),
      checkAuth: () => {
        if (!get().accessToken) set({ isAuthenticated: false });
      },
      setStreak: (streak) => set({ streak }),
      getAge: () => {
        if (!get().birthDate) return 25;
        const now = new Date(),
          birth = new Date(get().birthDate);
        let age = now.getFullYear() - birth.getFullYear();
        if (
          now.getMonth() < birth.getMonth() ||
          (now.getMonth() === birth.getMonth() && now.getDate() < birth.getDate())
        )
          age--;
        return age;
      },
    }),
    {
      name: 'aura-user-storage',
      version: 2,
      // Identity and private readings are reloaded after authentication, not trusted from disk.
      migrate: () => ({}),
      partialize: () => ({}),
    }
  )
);
